import realEstateIntroGraphic from "@/shared/assets/illustrations/property-valuables-intro.svg";
import { filterByCategory } from "@/shared/constants/estateCategories";

import type { FlowScreenConfig } from "@/shared/constants/flowTypes";
import type { ScreenFactoryContext } from "../../taskScreenRegistry";
import { AddProperty } from "@/pages/postloss/modules/property-valuables/components/AddProperty";
import { PropertiesReview } from "@/pages/postloss/modules/property-valuables/components/PropertiesReview";

import {
  getLoansQuestionComponent,
  getMortgageComponent,
  getHELOCComponent,
  getInsuranceQuestionComponent,
  getInsuranceFormComponent,
} from "../components/createPerPropertyComponents";
import { createModuleComplete } from "@/pages/postloss/modules/components/ModuleComplete";

export function createPropertiesScreens(
  firstName: string,
  context?: ScreenFactoryContext,
): FlowScreenConfig[] {
  const hasProperties = filterByCategory(context?.assets ?? [], "Properties").length > 0;

  return [
    {
      id: "real_estate_intro",
      layout: "full",
      imageSrc: realEstateIntroGraphic,
      imageSize: "md",
      imagePosition: "right",
      sectionLabel: "Section 1",
      title: `Let's add ${firstName}'s real estate`,
      subtitle: `Over the next few steps, we'll go through ${firstName}'s property. This helps us protect what they owned, keep payments on track, and make sure everything gets passed to the right people.`,
      verticalCenter: true,
      maxWidth: "4xl",
      getButtonText: () => "Get started",
    },
    {
      id: "add_properties_step",
      layout: "full",
      textAlign: "center",
      maxWidth: "2xl",
      hideButton: true,
      customComponent: PropertiesReview,
      shouldDisplay: ({ answers }) => answers["properties_review"] !== "none" || hasProperties,
    },
    {
      id: "has_property",
      layout: "full",
      textAlign: "center",
      maxWidth: "2xl",
      title: `Did ${firstName} own any real estate?`,
      subtitle: "This includes homes, rental properties, land, or vacation properties.",
      question: {
        type: "multiple_choice",
        required: true,
        options: [
          { value: "yes", label: "Yes, they owned property" },
          { value: "no", label: "No property" },
          { value: "not_sure", label: "Not sure" },
        ],
      },
      shouldDisplay: ({ answers }) => answers["properties_review"] === "none",
    },
    {
      id: "add_property",
      layout: "full",
      textAlign: "center",
      title: `Add ${firstName}'s property`,
      subtitle:
        "This helps us shape the right next steps for the property. Add what you have, and we'll help fill in the rest.",
      maxWidth: "4xl",
      hideButton: true,
      skipOnBack: true,
      customComponent: AddProperty,
      shouldDisplay: ({ answers }) =>
        answers["has_property"] === "yes" && answers["add_property"] !== "done",
    },
  ];
}

export function createPropertyLoansScreens(
  _firstName: string,
  context?: ScreenFactoryContext,
): FlowScreenConfig[] {
  const properties = filterByCategory(context?.assets ?? [], "Properties");
  const screens: FlowScreenConfig[] = [];

  for (let i = 0; i < properties.length; i++) {
    const pid = properties[i].id;

    screens.push(
      {
        id: `property_loans_question_${pid}`,
        layout: "full",
        maxWidth: "2xl",
        hideButton: true,
        customComponent: getLoansQuestionComponent(pid),
        shouldDisplay: ({ answers }) => answers["add_property"] === "done",
      },
      {
        id: `add_mortgage_${pid}`,
        layout: "full",
        maxWidth: "4xl",
        hideButton: true,
        customComponent: getMortgageComponent(pid),
        shouldDisplay: ({ answers }) => answers[`has_mortgage_${pid}`] === "yes",
      },
      {
        id: `add_heloc_${pid}`,
        layout: "full",
        maxWidth: "4xl",
        hideButton: true,
        customComponent: getHELOCComponent(pid),
        shouldDisplay: ({ answers }) => answers[`has_heloc_${pid}`] === "yes",
      },
    );
  }

  return screens;
}

export function createPropertyInsuranceScreens(
  _: string,
  context?: ScreenFactoryContext,
): FlowScreenConfig[] {
  const properties = filterByCategory(context?.assets ?? [], "Properties");
  const screens: FlowScreenConfig[] = [];

  for (let i = 0; i < properties.length; i++) {
    const pid = properties[i].id;

    screens.push(
      {
        id: `has_insurance_${pid}`,
        layout: "full",
        maxWidth: "2xl",
        hideButton: true,
        customComponent: getInsuranceQuestionComponent(pid),
        shouldDisplay: ({ answers }) => answers["add_property"] === "done",
      },
      {
        id: `add_insurance_${pid}`,
        layout: "full",
        maxWidth: "4xl",
        hideButton: true,
        customComponent: getInsuranceFormComponent(pid),
        shouldDisplay: ({ answers }) => answers[`has_insurance_${pid}`] === "yes",
      },
    );
  }

  screens.push(
    {
      id: "real_estate_review",
      layout: "full",
      textAlign: "center",
      maxWidth: "2xl",
      hideButton: true,
      customComponent: PropertiesReview,
      shouldDisplay: ({ answers }) => answers["add_property"] === "done",
    },
    {
      id: "real_estate_complete",
      layout: "full",
      textAlign: "center",
      hideButton: true,
      headerTitle: "",
      customComponent: createModuleComplete(),
    },
  );

  return screens;
}
